import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MessageCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface MessaggeriaLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Location {
  id: string;
  name: string;
}

interface Tavolo {
  id: string;
  table_number: number;
}

const MessaggeriaLoginModal: React.FC<MessaggeriaLoginModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [tavoli, setTavoli] = useState<Tavolo[]>([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadLocations();
      setSelectedLocation('');
      setSelectedTable('');
      setPin('');
      setError('');
      setTavoli([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedLocation) {
      loadTavoli();
    } else {
      setTavoli([]);
      setSelectedTable('');
    }
  }, [selectedLocation]);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('tavoli')
        .select('location_id, locations!inner(id, name, status)')
        .eq('locations.status', 'active');

      if (error) throw error;

      const uniqueLocations = new Map<string, Location>();
      (data || []).forEach((row: any) => {
        const loc = row.locations;
        if (loc && !uniqueLocations.has(loc.id)) {
          uniqueLocations.set(loc.id, { id: loc.id, name: loc.name });
        }
      });

      const sorted = Array.from(uniqueLocations.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setLocations(sorted);
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const loadTavoli = async () => {
    try {
      const { data, error } = await supabase
        .from('tavoli')
        .select('id, table_number')
        .eq('location_id', selectedLocation)
        .order('table_number');

      if (error) throw error;
      setTavoli(data || []);
    } catch (err) {
      console.error('Error loading tavoli:', err);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const tableNum = parseInt(selectedTable, 10);

      const { data, error } = await supabase
        .from('tavoli')
        .select('id, table_number, pin, location_id, locations!inner(name)')
        .eq('location_id', selectedLocation)
        .eq('table_number', tableNum)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setError('Tavolo non trovato.');
        return;
      }

      if (data.pin !== pin) {
        setError('PIN non valido.');
        return;
      }

      const locationName = (data as any).locations?.name || '';
      onClose();
      navigate(`/messaggeria?loc=${data.location_id}&tid=${data.id}&tn=${data.table_number}&lname=${encodeURIComponent(locationName)}`);
    } catch (err: any) {
      setError(err.message || 'Errore durante l\'accesso');
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (value: string) => {
    if (/^\d{0,4}$/.test(value)) {
      setPin(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Messaggeria</h2>
                <p className="text-white/90 text-sm">Accedi con il PIN del tuo tavolo</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleziona Locale
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-700 appearance-none cursor-pointer"
                required
              >
                <option value="">Scegli un locale</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleziona Tavolo
              </label>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-700 appearance-none cursor-pointer"
                required
                disabled={!selectedLocation}
              >
                <option value="">Scegli un tavolo</option>
                {tavoli.map((t) => (
                  <option key={t.id} value={t.table_number}>
                    Tavolo {t.table_number}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PIN
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-700 tracking-widest"
                placeholder="Inserisci PIN di 4 cifre"
                maxLength={4}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || !selectedLocation || !selectedTable || pin.length !== 4}
              className="w-full mt-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-rose-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Caricamento...' : 'Accedi'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Inserisci il locale, il numero del tavolo e il PIN fornito per accedere alla messaggeria.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessaggeriaLoginModal;
